import React, { useEffect, useState, useCallback } from "react";
import {
  Grid,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
} from "@mui/material";
import { useDispatchWrapper } from "../../hooks";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
} from "../../redux/messages/messagesActions";
import { ManageWhatsAppTemplateRightPanel } from "./ManageWhatsAppTemplateRightPanel";
import { useBillingCompanyContext } from "../../providers";
import { useDebounce } from "../../hooks/useDebounce";
import { PageLoader } from "../../components/PageLoader/PageLoader";
import { LoadState } from "../../constants/enums";

import { ViewToggle } from "../../components/ViewToggle";
import { useReduxStore } from "../../redux/store.hooks";
import {
  WhatsAppTemplateCardsSkeletonV2,
  WhatsAppTemplateCardsV2,
} from "../../components/WhatsAppTemplateTableV2";
import {
  fetchWhatsAppTemplateListAsync,
  syncMetaTemplates,
} from "../../redux/whatsappTemplate/whatsappTemplateAction";
import { WhatsappTemplatesAutoSearch } from "../../components/AutoCompleteSearches/WhatsappTemplatesAutoSearch";
import { ControlledCustomSelect } from "../../components/formsComponents";
import { StandardCard } from "../../components/Cards";
import { Breadcrumb } from "../../layout/fullLayout/breadcrumb/Breadcrumb";
import { IBreadcrumbProps } from "../../layout/fullLayout/breadcrumb/IBreadcrumbProps";
import { useNavigate } from "react-router-dom";
import {
  IWhatsAppTemplateData,
  TemplateForType,
} from "../../redux/whatsappTemplate/whatsappTemplate.types";
import { INITIAL_WHATSAPP_TEMPLATE_DATA } from "../../redux/whatsappTemplate/defaultState";
import { useDataTableV2Context } from "../../components/TableV2/context/DataTableV2Provider";
import { VIEWS_CACHING_KEYS } from "../../constants/constants";
import { Dialog } from "../../components/Dialogs/Dialog";
import { WhatsAppTemplateTable } from "../../components/WhatsAppTemplateTable/WhatsAppTemplateTable";
import { FilterAltOff, Refresh } from "@mui/icons-material";
import { TEMPLATE_FOR_OPTIONS } from "../../components/WhatsAppTemplateTable/components/TemplateForPopover";
import { NoRecordFound } from "../../components/NoRecordFound/NoRecordFound";

// 🔹 Utility: normalize meta template into consistent shape
const normalizeTemplate = (template: any): IWhatsAppTemplateData => {
  const components = template.components || [];
  return {
    ...template,
    header: components.find((c: any) => c.type === "HEADER") || {
      type: "HEADER",
      text: "",
      format: null,
    },
    body: components.find((c: any) => c.type === "BODY") || {
      type: "BODY",
      text: "",
    },
    footer: components.find((c: any) => c.type === "FOOTER") || {
      type: "FOOTER",
      text: "",
    },
    buttons: components.find((c: any) => c.type === "BUTTONS") || {
      type: "BUTTONS",
      buttons: [],
    },
  };
};

interface IWhatsAppTemplateListV2Props {
  onTemplateSelect?: (
    name: string,
    company_outlet_uuid: string,
    template: IWhatsAppTemplateData,
  ) => void;
  onTemplateDetails?: (template: IWhatsAppTemplateData) => void;
  selectedTemplateUuid?: string | null;
  showCreateButton?: boolean;
  showSelectionColumn?: boolean;

  onSearchChange?: (searchName: string) => void;
  isFromCampaign?: boolean;

  isFromChatModule?: {
    toNumber?: string;
    isFromStartConversation?: boolean;
    isFromQuickReply?: boolean;
    onClose?: () => void;
  };
}

export const WhatsAppTemplateListV2: React.FC<IWhatsAppTemplateListV2Props> = (
  props,
) => {
  const { saveDataInCache, cachingDataState, getCachedDataByKey } =
    useDataTableV2Context();
  const {
    onTemplateSelect,
    onTemplateDetails,
    selectedTemplateUuid = null,
    showCreateButton = true,
    showSelectionColumn = false,

    onSearchChange,
    isFromCampaign = false,

    isFromChatModule,
  } = props;

  const isEmbeddedInChat = Boolean(isFromChatModule);

  const BCrumb: IBreadcrumbProps["items"] = [
    {
      to: "/dashboard",
      title: "dashboard",
    },
    {
      to: "/meta-templates",
      title: "Meta Templates",
    },
  ];

  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();
  const { comapny } = useBillingCompanyContext() || {};
  const company_outlet_uuid = comapny?.company_outlet_uuid;

  const { list: whatsAppTemplateList, loading: dataLoading } =
    useReduxStore("whatsappTemplate").list;
  const { totalRecords: storeTotalRecords } =
    useReduxStore("whatsappTemplate").list;

  const [channels, setChannels] = useState<IWhatsAppTemplateData[]>([]);
  const [openManageWhatsAppTemplate, setOpenManageWhatsAppTemplate] =
    useState<IWhatsAppTemplateData | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [searchTemplateName, setSearchTemplateName] = useState("");
  const [status, setStatus] = React.useState("-1");
  const [category, setCategory] = React.useState("-1");
  const [templateFor, setTemplateFor] = React.useState<TemplateForType>(
    isFromChatModule?.toNumber ? "START_CONVERSATION" : "-1",
  );
  const [openSyncDialog, setOpenSyncDialog] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const lastDirectionRef = React.useRef<"next" | "prev" | null>(null);
  const [showCardsView, setShowCardsView] = useState(true);

  const debouncedSearchName = useDebounce(searchTemplateName, 500);

  const isFilterApplied =
    searchTemplateName !== "" ||
    templateFor !==
    (isFromChatModule?.toNumber ? "START_CONVERSATION" : "-1") ||
    (!isEmbeddedInChat && status !== "-1") ||
    category !== "-1";

  // 🔹 Fetch channels
  const getChannels = useCallback(
    async (
      channelType: string,
      showLoader?: boolean,
      direction?: "next" | "prev",
      newRowsPerPage?: number,
    ) => {
      try {
        if (showLoader) dispatch(saveLoaderProgress());
        setChannels([]);

        if (channelType === "WHATSAPP") {
          // Track direction for pagination update
          if (direction) {
            lastDirectionRef.current = direction;
          } else {
            setCurrentPage(1);
            lastDirectionRef.current = null;
          }

          const statusForFetch = isEmbeddedInChat ? "APPROVED" : status;

          await dispatch(
            fetchWhatsAppTemplateListAsync(
              // pageNumber
              direction === "next"
                ? currentPage + 1
                : direction === "prev"
                  ? currentPage - 1
                  : 1,
              // rowsInPerPage
              newRowsPerPage || rowsPerPage,
              // limit
              newRowsPerPage || rowsPerPage,
              // status (-1 = all; chat module always requests APPROVED)
              statusForFetch,
              // date
              { fromDate: "", toDate: "" },
              // searchValue (advanced filters)
              [],
              // company_outlet_uuid
              company_outlet_uuid,
              // searchByName
              debouncedSearchName,
              // searchByTemplateId
              undefined,
              //template_for
              templateFor,
              // onCallBack
              undefined,
              // showGlobalLoader
              showLoader ?? false,
              // category
              category,
            ),
          );
        }
      } finally {
        dispatch(saveLoaderCompleted());
      }
    },
    [
      company_outlet_uuid,
      debouncedSearchName,
      currentPage,
      rowsPerPage,
      status,
      isEmbeddedInChat,
      dispatch,
      templateFor,
      category,
    ],
  );

  // 🔹 Handlers

  const handleClearFilters = () => {
    setSearchTemplateName("");
    setTemplateFor(isFromChatModule?.toNumber ? "START_CONVERSATION" : "-1");
    if (!isEmbeddedInChat) {
      setStatus("-1");
    }
    setCategory("-1");
    setCurrentPage(1);
  };

  const handleTemplateSelect = useCallback(
    (
      name: string,
      company_outlet_uuid: string,
      template: IWhatsAppTemplateData,
    ) =>
      () => {
        const normalizedTemplate = normalizeTemplate(template);

        // Call onTemplateDetails if provided (for showing details view)
        if (onTemplateDetails) {
          onTemplateDetails(normalizedTemplate);
        }

        // Also call onTemplateSelect if provided (for other logic)
        if (onTemplateSelect) {
          onTemplateSelect(name, company_outlet_uuid, normalizedTemplate);
        }
      },
    [onTemplateSelect, onTemplateDetails],
  );

  const handleSearchChange = (template: any, inputValue: string) => {
    const newSearchName = inputValue || template?.name || "";

    if (onSearchChange) {
      onSearchChange(newSearchName);
    } else {
      setSearchTemplateName(newSearchName);
    }
  };

  const handleSyncMetaTemplates = () => {
    setOpenSyncDialog(true);
  };

  const handleConfirmSync = () => {
    setOpenSyncDialog(false);
    dispatch(
      syncMetaTemplates(company_outlet_uuid, (isSuccess) => {
        if (isSuccess) {
          getChannels("WHATSAPP", !showCardsView);
        }
      }),
    );
  };

  const handleShowCard = (value: boolean) => () => {
    saveDataInCache(VIEWS_CACHING_KEYS.META_TEMPLATE_GRID_TABLE_KEY, {
      showCard: value,
    });
    setShowCardsView(value);
  };

  useEffect(() => {
    // Reset to first page when search changes
    setCurrentPage(1);
    lastDirectionRef.current = null;
    getChannels("WHATSAPP", !showCardsView);
  }, [
    debouncedSearchName,
    templateFor,
    status,
    category,
    showCardsView,
    isEmbeddedInChat,
  ]);

  const handleCreate = () => {
    navigate("/meta-templates/manage");
  };

  useEffect(() => {
    const data = getCachedDataByKey(
      VIEWS_CACHING_KEYS.META_TEMPLATE_GRID_TABLE_KEY,
    );
    setShowCardsView(data ? data.showCard : true);
  }, [cachingDataState[VIEWS_CACHING_KEYS.META_TEMPLATE_GRID_TABLE_KEY]]);

  useEffect(() => {
    if (whatsAppTemplateList?.length) {
      setChannels(whatsAppTemplateList.map(normalizeTemplate));
    }
  }, [whatsAppTemplateList, debouncedSearchName]);

  useEffect(() => {
    if (storeTotalRecords !== undefined) {
      setTotalRecords(storeTotalRecords);
      // Update current page based on last direction
      if (lastDirectionRef.current === "next") {
        setCurrentPage((prev) => prev + 1);
        lastDirectionRef.current = null;
      } else if (lastDirectionRef.current === "prev") {
        setCurrentPage((prev) => Math.max(1, prev - 1));
        lastDirectionRef.current = null;
      }
    }
  }, [storeTotalRecords]);

  const isGridLoading =
    showCardsView &&
    dataLoading === LoadState.InProgress;

  return (
    <>
      {!isFromCampaign && (
        <Breadcrumb
          title="View Whatsapp Template"
          items={BCrumb}
          actionButtonText="Create Meta Template"
          actionButtonColor="success"
          onActionButtonClick={handleCreate}
        />
      )}

      <StandardCard>
        <PageLoader
          loading={!showCardsView && dataLoading === LoadState.InProgress}
        >
          <Grid container spacing={5} justifyContent="center">
            <Grid item xs={12}>
              {/* Desktop: Original side-by-side layout */}
              <Grid
                display={{ xs: "none", sm: "flex" }}
                justifyContent="space-between"
                alignItems="center"
              >
                <Box
                  sx={{
                    flex: 1,
                    maxWidth: 950,
                    fontWeight: "bold",
                    ml: 2,
                    display: "flex",
                    gap: 2,
                    mt: 1,
                  }}
                >
                  {/* <WhatsappTemplatesAutoSearch
                    label="Search by Template Name"
                    value={searchTemplateName}
                    onSelect={handleSearchChange}
                    templateFor={templateFor}
                  /> */}
                  <TextField
                    label="Search by Template Name"
                    value={searchTemplateName}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (onSearchChange) {
                        onSearchChange(val);
                      } else {
                        setSearchTemplateName(val);
                      }
                    }}
                    size="small"
                    sx={{ minWidth: 220, flex: 1 }}
                  />
                  <Box sx={{ display: "flex", gap: 2, flexShrink: 0 }}>
                    <Box sx={{ minWidth: 160, flex: 1 }}>
                      <ControlledCustomSelect
                        label="Type"
                        value={templateFor}
                        options={[
                          { label: "All", value: "-1" },
                          ...TEMPLATE_FOR_OPTIONS,
                        ]}
                        onChange={(e) =>
                          setTemplateFor(e.target.value as TemplateForType)
                        }
                      />
                    </Box>

                    {!isFromChatModule && (
                      <Box sx={{ minWidth: 160, flex: 1 }}>
                        <ControlledCustomSelect
                          label="Status"
                          value={status}
                          options={[
                            { label: "All", value: "-1" },
                            { label: "Approved", value: "APPROVED" },
                            { label: "Rejected", value: "REJECTED" },
                            { label: "Pending", value: "PENDING" },
                            { label: "Inactive", value: "INACTIVE" },
                          ]}
                          onChange={(e) => setStatus(e.target.value as string)}
                        />
                      </Box>
                    )}

                    {!isFromChatModule && (
                      <Box sx={{ minWidth: 170, flex: 1 }}>
                        <ControlledCustomSelect
                          label="Category"
                          value={category}
                          options={[
                            { label: "All", value: "-1" },
                            { label: "Marketing", value: "MARKETING" },
                            { label: "Authentication", value: "AUTHENTICATION" },
                            { label: "Utility", value: "UTILITY" },
                          ]}
                          onChange={(e) => setCategory(e.target.value as string)}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mr: 2,
                  }}
                >
                  {!isFromChatModule && (
                    <Button
                      variant="contained"
                      onClick={handleSyncMetaTemplates}
                    >
                      Sync Meta Templates
                    </Button>
                  )}

                  <ViewToggle
                    isGridView={showCardsView}
                    onToggle={handleShowCard(!showCardsView)}
                  />
                  <Tooltip title="Refresh List">
                    <IconButton
                      onClick={() => getChannels("WHATSAPP", !showCardsView)}
                    >
                      <Refresh />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Reset Filters">
                    <IconButton
                      onClick={handleClearFilters}
                      disabled={!isFilterApplied}
                      size="small"
                      sx={{
                        borderRadius: 1,
                        py: "6px",
                        ...(isFilterApplied
                          ? {
                            background: (theme) => theme.palette.warning.main,
                            "&:hover": {
                              background: "#ffa20f",
                            },
                          }
                          : {
                            background: "transparent",
                          }),
                      }}
                    >
                      <FilterAltOff />
                    </IconButton>
                  </Tooltip>

                  {showCreateButton && isFromCampaign && !isFromChatModule && (
                    <Button
                      variant="contained"
                      onClick={() =>
                        setOpenManageWhatsAppTemplate(
                          INITIAL_WHATSAPP_TEMPLATE_DATA,
                        )
                      }
                    >
                      Create Meta Template
                    </Button>
                  )}
                </Box>
              </Grid>

              {/* Mobile: Two-row layout with reduced spacing */}
              <Box
                sx={{
                  display: { xs: "block", sm: "none" },
                }}
              >
                {/* First Row: Search, Tags, Status filters */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    mb: 1.5,
                  }}
                >
                  <Box>
                    {/* <WhatsappTemplatesAutoSearch
                      label="Search by Template Name"
                      value={searchTemplateName}
                      onSelect={handleSearchChange}
                      templateFor={templateFor}
                    /> */}
                    <TextField
                      label="Search by Template Name"
                      value={searchTemplateName}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (onSearchChange) {
                          onSearchChange(val);
                        } else {
                          setSearchTemplateName(val);
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      flexDirection: "row",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <ControlledCustomSelect
                        label="Tags"
                        value={templateFor}
                        options={[
                          { label: "All", value: "-1" },
                          ...TEMPLATE_FOR_OPTIONS,
                        ]}
                        fullWidth={true}
                        onChange={(e) =>
                          setTemplateFor(e.target.value as TemplateForType)
                        }
                      />
                    </Box>
                    {!isFromChatModule && (
                      <Box sx={{ flex: 1 }}>
                        <ControlledCustomSelect
                          label="Status"
                          value={status}
                          options={[
                            { label: "All", value: "-1" },
                            { label: "Approved", value: "APPROVED" },
                            { label: "Rejected", value: "REJECTED" },
                            { label: "Pending", value: "PENDING" },
                            { label: "Inactive", value: "INACTIVE" },
                          ]}
                          fullWidth={true}
                          onChange={(e) => setStatus(e.target.value as string)}
                        />
                      </Box>
                    )}
                    {!isFromChatModule && (
                      <Box sx={{ flex: 1 }}>
                        <ControlledCustomSelect
                          label="Category"
                          value={category}
                          options={[
                            { label: "All", value: "-1" },
                            { label: "Marketing", value: "MARKETING" },
                            { label: "Authentication", value: "AUTHENTICATION" },
                            { label: "Utility", value: "UTILITY" },
                          ]}
                          fullWidth={true}
                          onChange={(e) => setCategory(e.target.value as string)}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Second Row: Sync Button and Toggle */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  {!isFromChatModule && (
                    <Button
                      variant="contained"
                      onClick={handleSyncMetaTemplates}
                    >
                      Sync Meta Templates
                    </Button>
                  )}
                  <ViewToggle
                    isGridView={showCardsView}
                    onToggle={handleShowCard(!showCardsView)}
                  />
                  {showCreateButton && isFromCampaign && !isFromChatModule && (
                    <Button
                      variant="contained"
                      onClick={() =>
                        setOpenManageWhatsAppTemplate(
                          INITIAL_WHATSAPP_TEMPLATE_DATA,
                        )
                      }
                    >
                      Create Meta Template
                    </Button>
                  )}
                </Box>
              </Box>

              <Box sx={{ width: "100%", p: 2 }}>
                {/* Template List/Cards View */}
                {!isGridLoading &&
                dataLoading !== LoadState.InProgress &&
                channels.length === 0 ? (
                  <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
                    <NoRecordFound />
                    {showCreateButton && !isFromChatModule && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          sx={{ ml: 1 }}
                          color="success"
                          variant="contained"
                          onClick={
                            isFromCampaign
                              ? () =>
                                  setOpenManageWhatsAppTemplate(
                                    INITIAL_WHATSAPP_TEMPLATE_DATA,
                                  )
                              : handleCreate
                          }
                        >
                          Create Meta Template
                        </Button>
                      </Box>
                    )}
                  </Box>
                ) : showCardsView ? (
                  isGridLoading ? (
                    <WhatsAppTemplateCardsSkeletonV2 count={rowsPerPage} />
                  ) : (
                    <WhatsAppTemplateCardsV2
                      fetchList={() => getChannels("WHATSAPP", !showCardsView)}
                      templates={channels}
                      onTemplateDetails={onTemplateDetails || (() => { })}
                      selectedTemplate={selectedTemplateUuid}
                      onTemplateSelect={handleTemplateSelect}
                      currentPage={currentPage}
                      totalRecords={totalRecords}
                      onPageChange={(dir, _, perPage) =>
                        getChannels("WHATSAPP", !showCardsView, dir, perPage)
                      }
                      onRowsPerPageChange={setRowsPerPage}
                      rowsPerPage={rowsPerPage}
                      onTemplatePreview={() => () => { }}
                      showCheckbox={isFromCampaign ? true : false}
                      isFromChatModule={isFromChatModule}
                    />
                  )
                ) : (
                  <WhatsAppTemplateTable
                    fetchList={() => getChannels("WHATSAPP", !showCardsView)}
                    templates={channels}
                    selectedTemplate={selectedTemplateUuid}
                    onTemplateSelect={handleTemplateSelect}
                    currentPage={currentPage}
                    totalRecords={totalRecords}
                    onPageChange={(dir, _, perPage) =>
                      getChannels("WHATSAPP", !showCardsView, dir, perPage)
                    }
                    onRowsPerPageChange={setRowsPerPage}
                    rowsPerPage={rowsPerPage}
                    onTemplatePreview={() => () => { }}
                    showSelectOption={isFromCampaign ? true : false}
                    isFromChatModule={isFromChatModule}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </PageLoader>
      </StandardCard>
      {openManageWhatsAppTemplate && (
        <ManageWhatsAppTemplateRightPanel
          open
          onClose={() => setOpenManageWhatsAppTemplate(null)}
          onSaveSuccess={() => {
            setOpenManageWhatsAppTemplate(null);
            getChannels("WHATSAPP", !showCardsView);
          }}
        />
      )}
      <Dialog
        open={openSyncDialog}
        onClose={() => setOpenSyncDialog(false)}
        title="Sync Meta Templates"
        size="sm"
        actions={[
          {
            label: "Okay",
            type: "button",
            variant: "contained",
            onClick: handleConfirmSync,
          },
        ]}
      >
        <Typography variant="body1">
          Meta may take around 24 hours to update the template approval status.
        </Typography>
      </Dialog>
    </>
  );
};
